// "Today" — the default landing section.
//
// A single question: what has happened to this patient in the last 24 hours,
// and what is waiting on me? It is the timeline windowed to today plus the
// documentation gaps and discharge indicators, because those are the two
// things a clinician wants prompted about rather than having to go looking for.
//
// Progressive disclosure: gaps and readiness collapse to a count until opened.
// Neither is a clinical mandate, and presenting them expanded by default would
// make a documentation checklist look like a set of orders.
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { LoadingState } from '@/components/ui/States';
import { useTimeline, useGapCheck, useDischargeReadiness } from '../hooks';
import type { CaseSheet, SourceRef } from '../types';
import { Section, Empty, fmtDateTime, fmtAgo } from '../ui';

export function TodayView({ admissionId, caseSheet, onOpen }: {
  admissionId: number; caseSheet: CaseSheet; onOpen?: (r: SourceRef) => void;
}) {
  // Memoized on admissionId only — recomputing this on every render mints a
  // new query key each time, which starves the timeline into a permanent
  // "loading" state under constant refetch pressure.
  const since = useMemo(() => new Date(Date.now() - 24 * 3600000).toISOString(), [admissionId]);
  const { data: timeline, isLoading } = useTimeline(admissionId, { from: since, limit: 60 });
  const { data: gaps } = useGapCheck(admissionId);
  const { data: readiness } = useDischargeReadiness(admissionId);

  const [showGaps, setShowGaps] = useState(false);
  const [showReadiness, setShowReadiness] = useState(false);

  const attention = readiness?.indicators.filter(i => i.state === 'attention') ?? [];

  return (
    <div className="space-y-4">
      {/* ── Documentation gaps ── */}
      {gaps && gaps.gaps.length > 0 && (
        <Section
          title="Documentation gaps"
          subtitle="Informational. Not every missing field is clinically required — these are prompts, not obligations."
          action={
            <Button variant="ghost" size="sm" onClick={() => setShowGaps(v => !v)}>
              {showGaps ? 'Hide' : `Show ${gaps.gaps.length}`}
            </Button>
          }
        >
          {showGaps ? (
            <ul className="space-y-1.5">
              {gaps.gaps.map(g => (
                <li key={g.key} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-border px-3 py-2">
                  <span className="text-[12.5px]">{g.message}</span>
                  <button
                    type="button"
                    className="text-[11.5px] font-medium text-teal underline-offset-2 hover:underline"
                    onClick={() => onOpen?.(g.target)}
                  >
                    {g.action}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-[12.5px] text-ink-muted">
              {gaps.gaps.length} item{gaps.gaps.length > 1 ? 's' : ''} not yet documented.
            </div>
          )}
        </Section>
      )}

      {/* ── Last 24 hours ── */}
      <Section
        title="Last 24 hours"
        subtitle={caseSheet.latest_vitals
          ? `Latest observation ${fmtAgo(caseSheet.latest_vitals.recorded_at)}`
          : 'No observations recorded'}
      >
        {isLoading ? <LoadingState label="Loading today’s activity…" />
          : !timeline?.data.length ? (
            <Empty label="Nothing recorded in the last 24 hours"
              hint="Vitals, notes, doses and results appear here as they are charted." />
          ) : (
            <ol className="list-none space-y-1 p-0">
              {timeline.data.map(item => (
                <li key={`${item.type}-${item.id}`}>
                  <button
                    type="button"
                    onClick={() => onOpen?.(item.source)}
                    className="w-full rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-muted"
                  >
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-[11px] tabular-nums text-ink-muted">{fmtDateTime(item.at)}</span>
                      <span className="text-[12.5px] font-medium">{item.title}</span>
                    </div>
                    {item.detail && <div className="mt-0.5 line-clamp-1 text-[12px] text-ink-muted">{item.detail}</div>}
                  </button>
                </li>
              ))}
            </ol>
          )}
      </Section>

      {/* ── Discharge readiness ── */}
      {readiness && (
        <Section
          title="Discharge readiness"
          subtitle={readiness.note}
          action={
            <Button variant="ghost" size="sm" onClick={() => setShowReadiness(v => !v)}>
              {showReadiness ? 'Hide' : attention.length ? `${attention.length} need attention` : 'All clear'}
            </Button>
          }
        >
          {showReadiness ? (
            <ul className="space-y-1.5">
              {readiness.indicators.map(i => (
                <li key={i.key} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-border px-3 py-2">
                  <span className="text-[12.5px]">{i.detail}</span>
                  <Badge variant={i.state === 'ok' ? 'success' : i.state === 'attention' ? 'warn' : 'neutral'}>
                    {i.state === 'ok' ? 'clear' : i.state}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-[12.5px] text-ink-muted">
              {attention.length
                ? `${attention.length} of ${readiness.indicators.length} indicators need attention.`
                : 'All indicators clear. The discharge decision remains the physician’s.'}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
